import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty, isNull } from 'lodash';
// eslint-disable-next-line import/no-named-as-default
import List, { type ListRowProps } from 'react-virtualized/dist/commonjs/List';
// Import Components
import { Button, Empty, Tab, Tabs } from '@geneui/components';
import NotificationItem from './NotificationItem';
// Import Types
import type { Notification } from 'components/Header/types';
// Import Constants
import { l } from 'constants/common';
// Import SCSS
import styles from './notificationPanel.module.scss';

const ROW_HEIGHT = 110;
const LIST_HEIGHT = 400;
const LIST_WIDTH = 420;
interface NewNotificationsButtonProps {
    count: number;
    onClick: () => void;
}
interface Props {
    notifications: Notification[];
    onClickNotification: (notification: Notification) => void;
    onClickMarkAsRead: (notification: Notification) => void;
    onClickMarkAsUnread: (notification: Notification) => void;
}

const NotificationList = ({ notifications, onClickNotification, onClickMarkAsRead, onClickMarkAsUnread }: Props) => {
    const NewNotificationsButton = ({ count, onClick }: NewNotificationsButtonProps) => {
        if (count === 0) return null;

        return (
            <Button className={styles.newBtn} onClick={onClick} appearance="grayscale">
                {count} {t(l.NewNotification)}
            </Button>
        );
    };
    const { t } = useTranslation();
    const isUserAtTopRef = useRef(true);
    const listVirtualRef = useRef<List | null>(null);

    const [newCount, setNewCount] = useState(0);
    const [initialCount, setInitialCount] = useState(notifications.length);

    const scrollToTop = () => {
        listVirtualRef.current?.scrollToRow(0);
    };

    const handleVirtualScroll = ({ scrollTop }: { scrollTop: number }) => {
        isUserAtTopRef.current = scrollTop < 20;
        if (isUserAtTopRef.current) {
            setNewCount(0);
        }
    };

    useEffect(() => {
        if (notifications.length > initialCount && !isUserAtTopRef.current) {
            setNewCount(notifications.length - initialCount);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notifications]);

    const handleShowNew = () => {
        setNewCount(0);
        setInitialCount(notifications.length);
        scrollToTop();
    };

    const unreadNotificationsList = useMemo(() => {
        return notifications.filter((n) => isNull(n.ReadDate));
    }, [notifications]);

    const rowRenderer = ({ index, key, style }: ListRowProps) => {
        const notification = notifications[index];

        return (
            <div key={key} style={style}>
                <NotificationItem
                    notification={notification}
                    onClick={onClickNotification}
                    onMarkAsRead={onClickMarkAsRead}
                    onUnmarkAsRead={onClickMarkAsUnread}
                />
            </div>
        );
    };

    const unreadRowRenderer = ({ index, key, style }: ListRowProps) => {
        const notification = unreadNotificationsList[index];

        return (
            <div key={key} style={style}>
                <NotificationItem
                    notification={notification}
                    onClick={onClickNotification}
                    onMarkAsRead={onClickMarkAsRead}
                    onUnmarkAsRead={onClickMarkAsUnread}
                />
            </div>
        );
    };

    return (
        <Tabs
            contentPadding="10"
            defaultActiveKey="1"
            className={styles.tabs}
            type="basic"
            contentClassName={styles.contentHolder}
        >
            <Tab
                key="1"
                title={
                    <div className={styles.tabTitle}>
                        <p>{t(l.All)}</p>
                        <span>{notifications.length ?? ''}</span>
                    </div>
                }
            >
                <NewNotificationsButton count={newCount} onClick={handleShowNew} />
                {isEmpty(notifications) ? (
                    <Empty title={t(l.NoNotificationsYet)} />
                ) : (
                    <List
                        className={styles.notificationList}
                        ref={listVirtualRef}
                        width={LIST_WIDTH}
                        height={LIST_HEIGHT}
                        rowCount={notifications.length}
                        rowHeight={ROW_HEIGHT}
                        rowRenderer={rowRenderer}
                        overscanRowCount={4} // render extra items above/below for smooth scrolling
                        onScroll={handleVirtualScroll}
                    />
                )}
            </Tab>
            <Tab
                key="2"
                title={
                    <div className={styles.tabTitle}>
                        <p>{t(l.Unread)}</p>
                        <span>{unreadNotificationsList.length ?? ''}</span>
                    </div>
                }
            >
                <NewNotificationsButton count={newCount} onClick={handleShowNew} />
                {isEmpty(unreadNotificationsList) ? (
                    <Empty title={t(l.NoNotificationsYet)} />
                ) : (
                    <List
                        className={styles.notificationList}
                        ref={listVirtualRef}
                        width={LIST_WIDTH}
                        height={LIST_HEIGHT}
                        rowCount={unreadNotificationsList.length}
                        rowHeight={ROW_HEIGHT}
                        rowRenderer={unreadRowRenderer}
                        overscanRowCount={4} // render extra items above/below for smooth scrolling
                        onScroll={handleVirtualScroll}
                    />
                )}
            </Tab>
        </Tabs>
    );
};

export default NotificationList;
